import { useState, useRef } from 'react';
import { motion, useMotionValue, useSpring, useTransform, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Eye, EyeOff, Mail, Lock, User, Clock, Droplets, ShieldCheck, FileText,
  Wrench, Building2, UserCog, ArrowRight, Waves, CheckCircle2
} from 'lucide-react';
import { MASCOT_URL } from '../assets/mascot';

const LOGO_URL = "/kardif-logo.png";

const ROLES = [
  { value: 'technician', label: 'Technicien', icon: Wrench },
  { value: 'secretary', label: 'Secrétaire', icon: Building2 },
  { value: 'admin', label: 'Admin', icon: UserCog },
];

const TRUST = [
  { icon: Droplets, label: 'Détection de fuites non destructive' },
  { icon: ShieldCheck, label: 'Rapports certifiés pour assurances' },
  { icon: FileText, label: 'Comptes-rendus PDF & Word' },
];

// ============ LOGO INTERACTIF (tilt 3D + brillance qui suit le curseur) ============
const InteractiveLogo = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rotateX = useSpring(useTransform(my, [0, 1], [12, -12]), { stiffness: 150, damping: 15 });
  const rotateY = useSpring(useTransform(mx, [0, 1], [-14, 14]), { stiffness: 150, damping: 15 });
  const shineX = useTransform(mx, [0, 1], ['0%', '100%']);
  const shineY = useTransform(my, [0, 1], ['0%', '100%']);

  const handleMove = (clientX, clientY) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    mx.set((clientX - r.left) / r.width);
    my.set((clientY - r.top) / r.height);
  };
  const reset = () => { mx.set(0.5); my.set(0.5); };

  return (
    <motion.div
      ref={ref}
      className="relative select-none"
      style={{ perspective: 800 }}
      onMouseMove={(e) => handleMove(e.clientX, e.clientY)}
      onMouseLeave={reset}
      onTouchMove={(e) => { const t = e.touches[0]; if (t) handleMove(t.clientX, t.clientY); }}
      onTouchEnd={reset}
      data-testid="interactive-logo"
      initial={{ opacity: 0, y: -20, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
    >
      {/* halo bronze pulsant */}
      <motion.div
        className="absolute -inset-8 rounded-full blur-2xl"
        style={{ background: 'radial-gradient(circle, rgba(197,160,89,0.35), transparent 70%)' }}
        animate={{ opacity: [0.4, 0.75, 0.4], scale: [0.95, 1.05, 0.95] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="relative rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-md p-6 sm:p-8 overflow-hidden"
        style={{ rotateX, rotateY, transformStyle: 'preserve-3d' }}
        animate={{ y: [0, -6, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
      >
        <img src={LOGO_URL} alt="KARDIF Investigation" className="h-24 sm:h-28 w-auto mx-auto drop-shadow-2xl" style={{ transform: 'translateZ(40px)' }} />
        {/* brillance qui suit le curseur */}
        <motion.div
          className="pointer-events-none absolute inset-0"
          style={{
            background: useTransform(
              [shineX, shineY],
              ([x, y]) => `radial-gradient(circle at ${x} ${y}, rgba(255,255,255,0.22), transparent 45%)`
            ),
          }}
        />
      </motion.div>
    </motion.div>
  );
};

// ============ FOND: gouttes d'eau / ondulations élégantes ============
const WaterBackdrop = () => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden">
    <div className="absolute inset-0" style={{ background: 'radial-gradient(circle at 50% -10%, rgba(197,160,89,0.14), transparent 55%)' }} />
    {[...Array(4)].map((_, i) => (
      <motion.div
        key={i}
        className="absolute rounded-full border border-[#C5A059]/20"
        style={{ width: 120 + i * 120, height: 120 + i * 120, left: '50%', top: '18%', x: '-50%', y: '-50%' }}
        animate={{ scale: [1, 1.6], opacity: [0.5, 0] }}
        transition={{ duration: 5, repeat: Infinity, delay: i * 1.25, ease: 'easeOut' }}
      />
    ))}
    {/* grain */}
    <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'60\' height=\'60\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")' }} />
  </div>
);

const LoginPage = () => {
  const { login, register } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pendingMessage, setPendingMessage] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ email: '', password: '', name: '', role: 'technician' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setPendingMessage(null);
    try {
      await login(loginForm.email, loginForm.password);
      toast.success('Connexion réussie !');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erreur de connexion';
      if (msg.includes('attente')) setPendingMessage(msg);
      else toast.error(msg);
    } finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true); setPendingMessage(null);
    try {
      const result = await register(registerForm.email, registerForm.password, registerForm.name, registerForm.role);
      if (result.status === 'pending') {
        setPendingMessage("Votre compte a été créé. Il est en attente d'approbation par l'administrateur.");
        toast.info('Compte créé - En attente d\'approbation');
      } else {
        toast.success('Compte créé avec succès !');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du compte');
    } finally { setLoading(false); }
  };

  const inputCls = "pl-12 h-12 text-base bg-white/[0.04] border-white/10 text-white placeholder:text-white/30 focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/30 rounded-xl";

  return (
    <div className="min-h-screen w-full bg-[#101215] relative flex items-center justify-center p-4 sm:p-6 font-[Plus_Jakarta_Sans]" data-testid="login-page">
      <WaterBackdrop />

      {/* Mascotte KARDIF */}
      {MASCOT_URL && (
        <img
          src={MASCOT_URL} alt="Mascotte KARDIF" data-testid="login-mascot"
          className="hidden md:block absolute bottom-0 left-4 lg:left-10 w-36 lg:w-48 h-auto drop-shadow-2xl z-20 pointer-events-none mascot-live"
        />
      )}

      <div className="relative z-10 w-full max-w-5xl grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">

        {/* ===== Colonne gauche: logo + accroche ===== */}
        <div className="flex flex-col items-center lg:items-start text-center lg:text-left">
          <InteractiveLogo />
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.6 }}
            className="mt-8"
          >
            <h1 className="font-[Space_Grotesk] text-3xl sm:text-4xl font-bold text-white tracking-tight leading-tight">
              KARDIF <span className="text-[#C5A059]">Investigation</span>
            </h1>
            <p className="mt-3 text-lg text-[#C5A059] font-medium flex items-center gap-2 justify-center lg:justify-start">
              <Waves className="h-5 w-5" /> Les experts des diagnostics d'eau
            </p>
            <div className="mt-6 space-y-2.5">
              {TRUST.map((t) => {
                const Icon = t.icon;
                return (
                  <div key={t.label} className="flex items-center gap-3 text-white/70 justify-center lg:justify-start">
                    <div className="w-8 h-8 rounded-lg bg-[#C5A059]/15 flex items-center justify-center flex-shrink-0">
                      <Icon className="h-4 w-4 text-[#C5A059]" />
                    </div>
                    <span className="text-sm">{t.label}</span>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>

        {/* ===== Colonne droite: carte de connexion (verre) ===== */}
        <motion.div
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.6 }}
          className="w-full"
        >
          {pendingMessage && (
            <div className="mb-4 rounded-2xl border border-amber-400/40 bg-amber-400/10 p-4 flex items-start gap-3" data-testid="pending-approval-notice">
              <Clock className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-300">En attente d'approbation</p>
                <p className="text-sm text-amber-200/80 mt-1">{pendingMessage}</p>
              </div>
            </div>
          )}

          <div className="rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)] overflow-hidden">
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-white/[0.03] rounded-none h-14 p-0 border-b border-white/10">
                {[{ v: 'login', l: 'Connexion' }, { v: 'register', l: 'Inscription' }].map((t) => (
                  <TabsTrigger key={t.v} value={t.v}
                    className="data-[state=active]:bg-[#C5A059] data-[state=active]:text-[#101215] text-white/60 rounded-none h-full text-base font-semibold transition-colors"
                    data-testid={`${t.v}-tab`}>
                    {t.l}
                  </TabsTrigger>
                ))}
              </TabsList>

              {/* CONNEXION */}
              <TabsContent value="login" className="mt-0 p-6 sm:p-8">
                <h2 className="font-[Space_Grotesk] text-2xl font-bold text-white tracking-tight">Bon retour</h2>
                <p className="text-white/50 text-sm mt-1 mb-6">Accédez à votre espace KARDIF</p>
                <form onSubmit={handleLogin} className="space-y-5">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-white/70">Email professionnel</Label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#C5A059]" />
                      <Input type="email" placeholder="votre@email.com" value={loginForm.email}
                        onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                        className={inputCls} required data-testid="login-email-input" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-white/70">Mot de passe</Label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#C5A059]" />
                      <Input type={showPassword ? 'text' : 'password'} placeholder="••••••••" value={loginForm.password}
                        onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                        className={`${inputCls} pr-12`} required data-testid="login-password-input" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-[#C5A059]">
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" disabled={loading} data-testid="login-submit-btn"
                    className="w-full h-14 bg-[#C5A059] hover:bg-[#D8B468] text-[#101215] text-base font-bold rounded-xl shadow-lg shadow-[#C5A059]/25 transition-all active:scale-[0.98]">
                    {loading ? 'Connexion...' : (<span className="flex items-center justify-center gap-2">Se connecter <ArrowRight className="h-5 w-5" /></span>)}
                  </Button>
                  <button type="button" onClick={() => { setLoginForm({ email: 'testadmin@kardif.com', password: 'Test123!' }); toast.info('Identifiants démo pré-remplis'); }}
                    className="w-full text-center text-xs text-white/40 hover:text-[#C5A059] transition-colors" data-testid="demo-fill-btn">
                    Utiliser un accès de démonstration
                  </button>
                </form>
              </TabsContent>

              {/* INSCRIPTION */}
              <TabsContent value="register" className="mt-0 p-6 sm:p-8">
                <h2 className="font-[Space_Grotesk] text-2xl font-bold text-white tracking-tight">Créer un compte</h2>
                <p className="text-white/50 text-sm mt-1 mb-6">Rejoignez l'équipe KARDIF</p>
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-white/70">Nom complet</Label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#C5A059]" />
                      <Input type="text" placeholder="Jean Dupont" value={registerForm.name}
                        onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                        className={inputCls} required data-testid="register-name-input" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-white/70">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#C5A059]" />
                      <Input type="email" placeholder="votre@email.com" value={registerForm.email}
                        onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                        className={inputCls} required data-testid="register-email-input" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-white/70">Mot de passe</Label>
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#C5A059]" />
                      <Input type={showPassword ? 'text' : 'password'} placeholder="••••••••" value={registerForm.password}
                        onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                        className={`${inputCls} pr-12`} required data-testid="register-password-input" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-[#C5A059]">
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-white/70">Rôle</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {ROLES.map((r) => {
                        const Icon = r.icon;
                        const active = registerForm.role === r.value;
                        return (
                          <button key={r.value} type="button" onClick={() => setRegisterForm({ ...registerForm, role: r.value })}
                            className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all active:scale-95 ${active ? 'border-[#C5A059] bg-[#C5A059]/15' : 'border-white/10 hover:border-[#C5A059]/40'}`}
                            data-testid={`role-${r.value}-card`}>
                            <Icon className={`h-5 w-5 ${active ? 'text-[#C5A059]' : 'text-white/40'}`} />
                            <span className={`text-xs font-semibold ${active ? 'text-white' : 'text-white/50'}`}>{r.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <Button type="submit" disabled={loading} data-testid="register-submit-btn"
                    className="w-full h-14 bg-[#C5A059] hover:bg-[#D8B468] text-[#101215] text-base font-bold rounded-xl shadow-lg shadow-[#C5A059]/25 transition-all active:scale-[0.98]">
                    {loading ? 'Création...' : 'Créer mon compte'}
                  </Button>
                  <p className="text-xs text-center text-white/40 pt-1">
                    Les comptes technicien et secrétaire nécessitent l'approbation de l'administrateur.
                  </p>
                </form>
              </TabsContent>
            </Tabs>
          </div>

          <p className="text-center text-xs text-white/30 mt-6">© 2026 KARDIF Investigation — Les experts des diagnostics d'eau</p>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;
